import defaultBackground from 'assets/default_explorer-background.png'
import { isEqual, uniq } from 'lodash-es'
import { useContext, useEffect, useState } from 'react'
import {
  ITrait,
  MediaWithUrl,
  SessionTrait,
  TemplateDocs,
  TemplateRoute,
  TemplateValueRoute,
  TraitGroupControl,
  TraitValue,
  WithRequired,
} from 'types'
import { SessionContext, useExplorerConfig } from '../components/providers'

export type TraitWithValues = WithRequired<ITrait & { route?: TemplateRoute }, 'values'>

export function getFilledTraitMap(docs: TemplateDocs | null | undefined) {
  if (!docs) return {}

  const { traits, template } = docs
  const traitMap = template.traitMap
  if (!traitMap) return {}

  return Object.keys(traitMap).reduce(
    (acc, key) => {
      const wrapper = traitMap[Number(key)]
      const trait = traits.find(t => t.id === wrapper.id)
      return { ...acc, [key]: { ...trait, ...wrapper } }
    },
    {} as { [key: number]: TraitWithValues }
  )
}

export function getTrait(docs: TemplateDocs, traitKey?: number): TraitWithValues | undefined {
  if (traitKey == null) return

  return getFilledTraitMap(docs)[traitKey] || {}
}

export function useRemainingRouting() {
  const { templateDocs } = useExplorerConfig()
  const traitsWithSelections = useTraitsWithSelections()

  let routing = templateDocs?.template.routing
  if (!routing) return

  for (let trait of traitsWithSelections) {
    const valueRoute = routing.valueRoutes?.find(route => {
      return route.fromIds?.some(id => trait.selected.some(s => s.traitId === id))
    }) as TemplateValueRoute | undefined

    routing = valueRoute?.to || routing?.default
    if (!routing) break
  }

  return routing
}

export interface TraitWithSelections extends ITrait {
  selected: SessionTrait[]
  step: TemplateRoute
}

export function useTraitsWithSelections(onlyShowActive?: boolean): TraitWithSelections[] {
  const { templateDocs } = useExplorerConfig()
  const { routeSteps, getLanguageValue, session } = useContext(SessionContext)

  if (!templateDocs) return []

  const currentStep = routeSteps[routeSteps.length - 1]
  let completedSteps = routeSteps.slice(0, -1)

  // Don't show select trait responses on current panel
  if (onlyShowActive && currentStep?.type === 'trait') {
    const currentTrait = getTrait(templateDocs, currentStep.key)
    if (currentTrait?.displayType === 'select') {
      while (true) {
        const lastIndex = completedSteps.length - 1
        const lastGroup = getTrait(templateDocs, completedSteps[lastIndex]?.key)
        if (lastGroup?.displayType !== 'select') break
        completedSteps.splice(lastIndex, 1)
      }
    }
  }

  const groups = completedSteps.map(step => {
    if (step.type !== 'trait') return

    const group = getTrait(templateDocs, step.key)
    if (!group) return

    const selected = session?.traits?.filter(s => {
      if (s.groupTitle !== getLanguageValue(group.title)) return false
      return group.values?.some(v => getLanguageValue(v.title) === s.title)
    })
    return { ...group, selected, step }
  })

  return groups.filter(Boolean) as TraitWithSelections[]
}

export interface IFormStep {
  key: number
  group: TraitWithValues
  position: StepPosition
}

export type StepPosition = 'complete' | 'current' | 'pending'

export function makeTraitPanels(
  docs: TemplateDocs | null | undefined,
  routeSteps: TemplateRoute[]
) {
  if (!docs?.template.routing) return []

  const { default: _, valueRoutes, ...firstRoute } = docs.template.routing
  const traitMap = getFilledTraitMap(docs)
  let completedSteps = [...routeSteps]
  if (!completedSteps.length) completedSteps = [firstRoute]
  const pendingKeys = Object.keys(traitMap)
    .map(k => Number(k))
    .filter(key => completedSteps.every(s => s.key !== key))
  const currentStep = completedSteps.pop()

  function makeStep(key: number, position: StepPosition): IFormStep {
    const group = traitMap[key]
    if (group.displayType === 'select') return { key, group, position: 'complete' }
    return { key, group, position }
  }

  const currentTrait =
    currentStep?.type === 'trait' ? makeStep(currentStep.key, 'current') : undefined
  const completedTraits = completedSteps.map(s => makeStep(s.key, 'complete'))
  const pendingTraits = pendingKeys.map(key => makeStep(key, 'pending'))

  return [...(currentTrait ? [currentTrait] : []), ...completedTraits, ...pendingTraits]
}

export function getTraitsByControl(
  control: TraitGroupControl,
  traitMaps: TraitWithValues[]
): TraitValue[] {
  return traitMaps.reduce((acc, cur) => {
    if (cur.control === control) {
      return [...acc, ...cur.values]
    }
    return acc
  }, [] as TraitValue[])
}

export function useAllBackgrounds() {
  const { templateDocs } = useExplorerConfig()
  if (!templateDocs) return []

  const traitMap = getFilledTraitMap(templateDocs)
  const traitWrappers = getTraitsByControl('background', Object.values(traitMap))
  const traitMediaIds = traitWrappers.map(t => t.media)
  const traitGroupMediaIds = Object.values(traitMap).map(g => g.media)
  const mediaIds = [...traitMediaIds, ...traitGroupMediaIds]

  return uniq(mediaIds)
    .map(id => templateDocs.media.find(m => m.id === id))
    .filter(Boolean) as MediaWithUrl[]
}

export function useCurrentBackgrounds(selections?: SessionTrait[]) {
  const { templateDocs } = useExplorerConfig()
  const { session, routeSteps } = useContext(SessionContext)
  const allBackgrounds = useAllBackgrounds()
  const traitMap = getFilledTraitMap(templateDocs)

  const [backgrounds, setBackgrounds] = useState<MediaWithUrl[]>([])

  const _selections = selections || session?.traits

  useEffect(() => {
    if (!templateDocs) return

    let _backgrounds: MediaWithUrl[] = []

    for (let step of routeSteps) {
      if (step.type !== 'trait') break

      const group = traitMap[step.key]
      const trait = group.values?.find(p => _selections?.some(s => s.title === p.title?.en))
      if (trait?.media && group.control === 'background') {
        const background = allBackgrounds.find(b => b.id === trait.media)
        if (background) _backgrounds.push(background)
      } else if (group.media) {
        const background = allBackgrounds.find(b => b.id === group.media)
        if (background) _backgrounds.push(background)
      }
    }

    if (!isEqual(_backgrounds, backgrounds)) setBackgrounds(_backgrounds)
  }, [traitMap, routeSteps, _selections])

  return backgrounds
}

export function useDefaultBackground() {
  const { templateDocs } = useExplorerConfig()
  const backgroundMedia = templateDocs?.template.backgroundMedia
  if (!backgroundMedia) return defaultBackground

  const mediaDoc = templateDocs.media.find(m => m.id === backgroundMedia)
  return mediaDoc?.url
}
